/**
 * Parses `submit_plan_for_approval` tool-call args and pending tool result.
 */

export interface PlanApprovalArgs {
  userRequest: string
  steps: string[]
  planId: string | null
}

export type PlanApprovalDecision = 'approved' | 'rejected'

/** Matches a leading `Step 1: ` / `Step 12: ` style prefix from the agent. */
const STEP_LABEL_PREFIX = /^Step\s+\d+:\s*/i

/** Matches a leading `1. ` / `12. ` numbered-list prefix from the agent. */
const NUMBERED_LIST_PREFIX = /^\d+\.\s+/

/** Bound pathological agent step text before normalization (Sonar S5852). */
const MAX_PLAN_STEP_LENGTH = 4096

/**
 * Flattens whitespace and strips a redundant step number prefix when present
 * (`Step {n}: ` or `{n}. `).
 */
export const normalizePlanStepText = (step: string): string => {
  const bounded = step.length > MAX_PLAN_STEP_LENGTH ? step.slice(0, MAX_PLAN_STEP_LENGTH) : step
  const flattened = bounded
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join(' ')
    .trim()
  return flattened.replace(STEP_LABEL_PREFIX, '').replace(NUMBERED_LIST_PREFIX, '').trim()
}
/**
 * Coerces tool-call arguments into card-ready plan fields.
 */
export const parsePlanApprovalArgs = (args: Record<string, unknown>): PlanApprovalArgs => {
  const userRequest = typeof args.user_request === 'string' ? args.user_request : ''
  const steps = Array.isArray(args.steps)
    ? args.steps
        .filter((step): step is string => typeof step === 'string')
        .map(normalizePlanStepText)
        .filter((step) => step.length > 0)
    : []
  const planId = typeof args.plan_id === 'string' && args.plan_id.trim().length > 0 ? args.plan_id : null

  return { userRequest, steps, planId }
}

/**
 * Reads decision / plan_id from a long-running tool result payload (JSON string or object).
 */
export const parsePlanApprovalResult = (
  content: unknown
): { status: PlanApprovalDecision | 'pending' | null; planId: string | null } => {
  let parsed: unknown = content
  if (typeof content === 'string') {
    try {
      parsed = JSON.parse(content)
    } catch {
      return { status: null, planId: null }
    }
  }

  if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { status: null, planId: null }
  }

  const record = parsed as Record<string, unknown>
  const statusRaw = typeof record.status === 'string' ? record.status : null
  const status = statusRaw === 'approved' || statusRaw === 'rejected' || statusRaw === 'pending' ? statusRaw : null
  const planId = typeof record.plan_id === 'string' && record.plan_id.trim().length > 0 ? record.plan_id : null

  return { status, planId }
}

/**
 * Builds a chat message that tells the agent the user decided on the plan.
 * Prefer a plain user turn over AG-UI tool resume — simpler and avoids LRO
 * FunctionResponse plumbing through Flow's Agent Engine proxy.
 */
export const buildPlanApprovalUserMessage = (decision: PlanApprovalDecision): string => {
  if (decision === 'approved') {
    return 'Plan approved — please go ahead with the analysis.'
  }
  return 'Plan rejected.'
}
